import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Button } from "components/ui/button";
import { Switch } from "components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Progress } from "components/ui/progress";
import { Badge } from "components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { CloudUpload, Loader2, AlertCircle } from "lucide-react";
import { FlowPicker } from "components/admin/FlowPicker";
import { importFlowFromCsv, importFlowFromDrive, getImportStatus, cancelImport, exportFlow, listImportJobs } from "../../services/api";
import type { FlowImportErrorDetail, ImportJobSummary, ImportStatus } from "../../services/api";
import { adminContent } from "../../services/api";
import { useToast } from "components/ui/use-toast";

type ImportSource = "drive" | "csv";

interface AdminFlow {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  frequency?: string | null;
  is_quiz: boolean;
  passing_score_percentage?: number | null;
  max_attempts?: number | null;
  estimated_duration_minutes?: number | null;
}

export const getFlowImportErrorRow = (error: Partial<FlowImportErrorDetail>): string => {
  const csvRow = error.row_number ?? error.line_number ?? error.csv_row_number;
  if (csvRow !== undefined && csvRow !== null) {
    return String(csvRow);
  }

  if (error.row_index !== undefined && error.row_index !== null && error.row_index !== "") {
    const numericIndex = Number(error.row_index);
    return Number.isInteger(numericIndex)
      ? String(numericIndex + 2)
      : String(error.row_index);
  }

  return "-";
};

export const getFlowImportErrorQuestion = (error: Partial<FlowImportErrorDetail>): string =>
  String(error.key || error.question_key || error.question_text?.substring(0, 50) || "-");

export const getFlowImportErrorMessage = (error: Partial<FlowImportErrorDetail>): string =>
  error.message || error.error_message || "Unknown import error.";

export const APPEND_MODE_HELP_TEXT =
  "Preserve existing question positions, update matching keys in place, and add new keys at the end.";

const terminalStatuses = new Set(["completed", "failed", "cancelled", "error"]);

const statusVariantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  in_progress: "default",
  completed: "secondary",
  failed: "destructive",
  cancelled: "outline",
  error: "destructive",
};

interface FlowImportProps {
  hideExportButton?: boolean;
}

const FlowImport: React.FC<FlowImportProps> = ({ hideExportButton = false }) => {
  const { addToast } = useToast();

  // Flow selection state
  const [flows, setFlows] = useState<AdminFlow[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string>("");
  const [createNewFlow, setCreateNewFlow] = useState(false);
  const [flowSearchTerm, setFlowSearchTerm] = useState("");
  const [isLoadingFlows, setIsLoadingFlows] = useState(false);
  const flowsRequestRef = useRef(0);
  const selectedFlowRef = useRef<AdminFlow | null>(null);

  // New flow creation state
  const [newFlowName, setNewFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [isQuiz, setIsQuiz] = useState(false);
  const [passingScore, setPassingScore] = useState<number>(70);
  const [maxAttempts, setMaxAttempts] = useState<number | undefined>(undefined);
  const [estimatedDuration, setEstimatedDuration] = useState<number | undefined>(undefined);

  // Import options state
  const [source, setSource] = useState<ImportSource>("csv");
  const [driveUrl, setDriveUrl] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [replaceMedia, setReplaceMedia] = useState(false);
  const [appendMode, setAppendMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Progress tracking state
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState<ImportStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const lastNotifiedStatusRef = useRef<string | null>(null);

  // Recent job history
  const [recentJobs, setRecentJobs] = useState<ImportJobSummary[]>([]);

  const refreshRecentJobs = useCallback(async () => {
    try {
      const jobs = await listImportJobs('flow');
      setRecentJobs(jobs);
    } catch {
      // silent — non-critical
    }
  }, []);

  // Load flows based on search term (server-side search)
  const loadFlows = useCallback(async (searchTerm: string = "") => {
    const requestId = ++flowsRequestRef.current;
    setIsLoadingFlows(true);
    try {
      const summaries: AdminFlow[] = [];
      let page = 1;
      let hasNextPage: boolean;
      do {
        const response = await adminContent.flows.listSummary({
          search: searchTerm,
          page_size: 100,
          page,
        });
        if (requestId !== flowsRequestRef.current) return;
        summaries.push(...response.data.results);
        hasNextPage = Boolean(response.data.next);
        page += 1;
      } while (hasNextPage);
      setFlows(summaries);
    } catch (error) {
      if (requestId !== flowsRequestRef.current) return;
      console.error("Failed to load flows", error);
      addToast({
        title: "Unable to load modules",
        description: "Please refresh the page or try again later.",
        variant: "destructive",
      });
    } finally {
      if (requestId === flowsRequestRef.current) setIsLoadingFlows(false);
    }
  }, [addToast]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadFlows(flowSearchTerm);
    }, 300); // Wait 300ms after user stops typing

    return () => {
      clearTimeout(timeoutId);
      flowsRequestRef.current += 1;
    };
  }, [flowSearchTerm, loadFlows]);

  // Load recent jobs on mount
  useEffect(() => {
    refreshRecentJobs();
  }, [refreshRecentJobs]);

  // Poll import status
  const pollStatus = useCallback(
    async (jobId: number) => {
      try {
        const statusResponse = await getImportStatus(jobId);

        let derivedStatus = statusResponse.status;
        const hasError = Boolean(statusResponse.error);
        if (hasError && (!derivedStatus || !terminalStatuses.has(derivedStatus))) {
          derivedStatus = "failed";
        }

        const normalizedProgress = derivedStatus
          ? { ...statusResponse, status: derivedStatus }
          : statusResponse;

        setProgress(normalizedProgress);

        const isTerminal = Boolean(
          derivedStatus && terminalStatuses.has(derivedStatus)
        );

        if (isTerminal || hasError) {
          setIsPolling(false);

          const statusForNotification = derivedStatus ?? (hasError ? "failed" : null);
          if (
            statusForNotification &&
            lastNotifiedStatusRef.current !== statusForNotification
          ) {
            if (statusForNotification === "failed" || statusForNotification === "error") {
              addToast({
                title: "Import failed",
                description:
                  statusResponse.error ||
                  statusResponse.details ||
                  "Check the import details for more information.",
                variant: "destructive",
              });
            } else if (statusForNotification === "completed") {
              addToast({
                title: "Import completed",
                description: "Module import completed successfully.",
              });
              // Reload flows list
              loadFlows();
            }

            lastNotifiedStatusRef.current = statusForNotification;
          }
        } else if (derivedStatus && derivedStatus !== lastNotifiedStatusRef.current) {
          lastNotifiedStatusRef.current = derivedStatus;
        }
      } catch (error) {
        console.error("Failed to fetch import status", error);
        addToast({
          title: "Unable to refresh status",
          description: "Polling has been paused. Try again shortly.",
          variant: "destructive",
        });
        setIsPolling(false);
        lastNotifiedStatusRef.current = "error";
      }
    },
    [addToast, loadFlows]
  );

  useEffect(() => {
    if (!isPolling || !currentJobId) return;

    const interval = setInterval(() => {
      pollStatus(currentJobId);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPolling, currentJobId, pollStatus]);

  const handleSubmit = async () => {
    // Validation
    if (!createNewFlow && !selectedFlowId) {
      addToast({
        title: "Module selection required",
        description: "Please select an existing module or create a new one.",
        variant: "destructive",
      });
      return;
    }

    if (createNewFlow && !newFlowName.trim()) {
      addToast({
        title: "Module name required",
        description: "Please enter a name for the new module.",
        variant: "destructive",
      });
      return;
    }

    // Check if flow name already exists when creating new flow
    if (createNewFlow && flows.some(flow => flow.name.toLowerCase() === newFlowName.trim().toLowerCase())) {
      addToast({
        title: "Module name already exists",
        description: "A module with this name already exists. Please choose a different name or select the existing module to update it.",
        variant: "destructive",
      });
      return;
    }

    if (source === "csv" && !csvFile) {
      addToast({
        title: "CSV file required",
        description: "Please select a CSV file to import.",
        variant: "destructive",
      });
      return;
    }

    if (source === "drive" && !driveUrl.trim()) {
      addToast({
        title: "Drive URL required",
        description: "Please enter a Google Drive folder URL.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let response;

      const flowData: any = {
        flow_id: !createNewFlow && selectedFlowId ? Number(selectedFlowId) : undefined,
        flow_name: createNewFlow ? newFlowName : undefined,
        flow_description: createNewFlow ? flowDescription.trim() : undefined,
        frequency: createNewFlow ? frequency : undefined,
        is_quiz: createNewFlow ? isQuiz : undefined,
        passing_score_percentage: createNewFlow && isQuiz ? passingScore : undefined,
        max_attempts: createNewFlow && isQuiz && maxAttempts ? maxAttempts : undefined,
        estimated_duration_minutes: createNewFlow && estimatedDuration ? estimatedDuration : undefined,
        replace_existing_media: replaceMedia,
        append_mode: appendMode,
      };

      if (source === "csv" && csvFile) {
        response = await importFlowFromCsv({
          ...flowData,
          csv_file: csvFile,
        });
      } else if (source === "drive") {
        response = await importFlowFromDrive({
          ...flowData,
          drive_url: driveUrl,
        });
      }

      if (response && response.job_id) {
        setCurrentJobId(response.job_id);
        setIsPolling(true);
        lastNotifiedStatusRef.current = null;
        refreshRecentJobs();
        addToast({
          title: "Import started",
          description: "Module import has been queued and will start shortly.",
        });
      }
    } catch (error: any) {
      console.error("Import failed:", error);
      addToast({
        title: "Import failed",
        description: error?.response?.data?.error || error.message || "Unknown error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelImport = async () => {
    if (!currentJobId) return;

    setIsCancelling(true);
    try {
      await cancelImport(currentJobId);
      addToast({
        title: "Import cancelled",
        description: "The import has been cancelled.",
      });
      setIsPolling(false);
      setProgress(null);
      setCurrentJobId(null);
    } catch (error: any) {
      console.error("Cancel failed:", error);
      addToast({
        title: "Cancel failed",
        description: error?.response?.data?.error || "Failed to cancel the import.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleExport = async () => {
    if (!selectedFlowId) {
      addToast({
        title: "No module selected",
        description: "Please select a module to export.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await exportFlow(Number(selectedFlowId));
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Use flow name for filename, sanitize it for file system
      const selectedFlow = flows.find(f => f.id.toString() === selectedFlowId) ?? selectedFlowRef.current;
      const flowName = selectedFlow?.name || `flow_${selectedFlowId}`;
      const sanitizedName = flowName.replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
      a.download = `${sanitizedName}.csv`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      addToast({
        title: "Export successful",
        description: "Module has been exported to CSV.",
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      addToast({
        title: "Export failed",
        description: error?.response?.data?.error || "Failed to export the module.",
        variant: "destructive",
      });
    }
  };

  const progressPercentage =
    progress && progress.total_items > 0
      ? Math.round((progress.processed_items / progress.total_items) * 100)
      : 0;

  const flowOptions = useMemo(
    () =>
      flows.map((flow) => ({
        value: flow.id.toString(),
        label: flow.name,
        searchText: `${flow.name} ${flow.description ?? ""}`.trim(),
        isQuiz: flow.is_quiz,
      })),
    [flows]
  );

  const flowEmptyMessage = isLoadingFlows
    ? "Loading modules..."
    : flows.length === 0 && flowSearchTerm
      ? `No modules found matching "${flowSearchTerm}"`
      : "No modules available";

  return (
    <div className="space-y-6">
      <CardTitle
        title="Module Import"
        subtitle="Import questions into a module from a CSV file or Google Drive"
        size="default"
      />
          {/* Flow Selection Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="create-new-flow"
                checked={createNewFlow}
                onCheckedChange={setCreateNewFlow}
              />
              <Label htmlFor="create-new-flow">Create new module</Label>
            </div>

            {!createNewFlow ? (
              <div className="space-y-2">
                <Label htmlFor="flow-select">Select Existing Module</Label>
                <FlowPicker
                  value={selectedFlowId}
                  onValueChange={(value) => {
                    selectedFlowRef.current = flows.find((flow) => flow.id.toString() === value) ?? null;
                    setSelectedFlowId(value);
                  }}
                  options={flowOptions}
                  placeholder="Choose a module to import into"
                  triggerId="flow-select"
                  searchTerm={flowSearchTerm}
                  onSearchTermChange={setFlowSearchTerm}
                  loading={isLoadingFlows}
                  emptyMessage={flowEmptyMessage}
                />
                {!hideExportButton && (
                  <Button
                    variant="secondary"
                    size="auto"
                    onClick={handleExport}
                    disabled={!selectedFlowId}
                    className="mt-2"
                  >
                    Export Selected Module
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4 rounded-lg border border-border/40 p-4">
                <div className="space-y-2">
                  <Label htmlFor="new-flow-name">Module Name *</Label>
                  <Input
                    id="new-flow-name"
                    placeholder="e.g., daily_reflection"
                    value={newFlowName}
                    onChange={(e) => setNewFlowName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="flow-description">Description</Label>
                  <Textarea
                    id="flow-description"
                    placeholder="Describe the purpose of this module"
                    value={flowDescription}
                    onChange={(e) => setFlowDescription(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequency</Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger id="frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Once</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="on_demand">On Demand</SelectItem>
                        <SelectItem value="onboarding">Onboarding</SelectItem>
                        <SelectItem value="every_login">Every Login</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimated-duration">Estimated Duration (minutes)</Label>
                    <Input
                      id="estimated-duration"
                      type="number"
                      placeholder="Optional"
                      value={estimatedDuration ?? ""}
                      onChange={(e) =>
                        setEstimatedDuration(e.target.value ? Number(e.target.value) : undefined)
                      }
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch id="is-quiz" checked={isQuiz} onCheckedChange={setIsQuiz} />
                    <Label htmlFor="is-quiz">This is a quiz module</Label>
                  </div>

                  {isQuiz && (
                    <div className="grid gap-4 md:grid-cols-2 pl-6">
                      <div className="space-y-2">
                        <Label htmlFor="passing-score">Passing Score (%)</Label>
                        <Input
                          id="passing-score"
                          type="number"
                          min="0"
                          max="100"
                          value={passingScore}
                          onChange={(e) => setPassingScore(Number(e.target.value))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="max-attempts">Max Attempts (optional)</Label>
                        <Input
                          id="max-attempts"
                          type="number"
                          placeholder="Unlimited"
                          value={maxAttempts ?? ""}
                          onChange={(e) =>
                            setMaxAttempts(e.target.value ? Number(e.target.value) : undefined)
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Import Source Section */}
          <div>
            <CardTitle title="Import Source" size="small"/>
            <Tabs value={source} onValueChange={(v) => setSource(v as ImportSource)}>
              <TabsList className="bg-transparent">
                <TabsTrigger value="csv" className="px-2">CSV Upload</TabsTrigger>
                <TabsTrigger value="drive" className="px-2">Google Drive</TabsTrigger>
              </TabsList>

              <TabsContent value="csv" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="csv-file">CSV File</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    className="bg-white/80"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  />
                  <p className="text-xs text-muted-foreground">
                    CSV should contain question fields (Question type, Text, Key, Option1..OptionN, etc.) and may include
                    quiz config columns per module: Is Quiz, Passing Score Percentage, Max Attempts, Allow Retake After Pass,
                    Randomize Question Order, Randomize Option Order, Estimated Duration Minutes, and Correct Option Indexes.
                    Module import ignores the Name and Flow Required columns and imports into the selected target module.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="drive" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="drive-url">Google Drive Folder URL</Label>
                  <Input
                    id="drive-url"
                    placeholder="https://drive.google.com/drive/folders/..."
                    value={driveUrl}
                    onChange={(e) => setDriveUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Folder should contain a CSV file and any referenced media files.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Import Options */}
          <div className="space-y-4">
            <CardTitle title="Import Options" size="small"/>
            <div className="space-y-3">
              {!createNewFlow && (
                <div className="flex items-start space-x-2">
                  <Switch
                    id="append-mode"
                    checked={appendMode}
                    onCheckedChange={setAppendMode}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="append-mode" className="font-normal">
                      Append to existing module (off replaces all questions)
                    </Label>
                    <p className="text-xs text-muted-foreground">{APPEND_MODE_HELP_TEXT}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  id="replace-media"
                  checked={replaceMedia}
                  onCheckedChange={setReplaceMedia}
                />
                <Label htmlFor="replace-media" className="font-normal">
                  Replace existing media in storage
                </Label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isPolling}
            className="w-full md:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting Import...
              </>
            ) : (
              <>
                <CloudUpload className="mr-2 h-4 w-4" />
                Import Module
              </>
            )}
          </Button>

      {/* Progress Section */}
      {(progress || recentJobs.length > 0) && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <CardTitle title="Import Progress" size="default" />
            <div className="flex items-center gap-3">
              {recentJobs.length > 0 && (
                <Select
                  value={currentJobId?.toString() ?? ""}
                  onValueChange={(value) => {
                    const jobId = Number(value);
                    const job = recentJobs.find((j) => j.id === jobId);
                    setCurrentJobId(jobId);
                    setProgress(null);
                    lastNotifiedStatusRef.current = null;
                    if (job && !terminalStatuses.has(job.status)) {
                      setIsPolling(true);
                    } else {
                      setIsPolling(false);
                      pollStatus(jobId);
                    }
                  }}
                >
                  <SelectTrigger
                    aria-label="View previous module import"
                    className="w-full sm:w-[17.5rem]"
                  >
                    <SelectValue placeholder="View a previous import…" />
                  </SelectTrigger>
                  <SelectContent>
                    {recentJobs.map((job) => (
                      <SelectItem key={job.id} value={job.id.toString()}>
                        #{job.id} — {job.course_name}{" "}
                        {job.started_at
                          ? new Date(job.started_at).toLocaleDateString()
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {progress && (
                <Badge variant={statusVariantMap[progress.status] || "default"}>
                  Status: {progress.status.replace("_", " ")}
                </Badge>
              )}
            </div>
          </div>

          {progress ? (
            <div className="space-y-4">
              {progress.status === "in_progress" && (
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    size="auto"
                    onClick={handleCancelImport}
                    disabled={isCancelling}
                  >
                    {isCancelling ? "Cancelling..." : "Cancel Import"}
                  </Button>
                </div>
              )}

              {progress.total_items > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Progress</span>
                    <span className="font-semibold">
                      {progress.processed_items} / {progress.total_items} questions ({progressPercentage}%)
                    </span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>
              )}

              {progress.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{progress.error}</AlertDescription>
                </Alert>
              )}

              {progress.error_details && progress.error_details.length > 0 && (
                <div className="min-w-0 space-y-2">
                  <h4 id="flow-import-error-details-heading" className="text-sm font-semibold">
                    Error Details ({progress.error_details.length} errors)
                  </h4>
                  <div
                    aria-labelledby="flow-import-error-details-heading"
                    className="max-h-60 min-w-0 w-full overflow-auto rounded-lg border border-destructive/40 bg-destructive/5 [&>div]:overflow-visible"
                    data-testid="flow-import-error-details"
                    role="region"
                    tabIndex={0}
                  >
                    <Table className="min-w-[30rem] table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Row</TableHead>
                          <TableHead className="w-48">Question</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {progress.error_details.slice(0, 50).map((err, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="align-top">{getFlowImportErrorRow(err)}</TableCell>
                            <TableCell className="break-words align-top">
                              {getFlowImportErrorQuestion(err)}
                            </TableCell>
                            <TableCell className="break-words align-top text-xs">
                              {getFlowImportErrorMessage(err)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {progress.created_at && (
                <div className="grid grid-cols-3 gap-4 text-sm text-muted-foreground">
                  <div>
                    <span className="font-medium">Started:</span>{" "}
                    {new Date(progress.created_at).toLocaleString()}
                  </div>
                  {progress.updated_at && (
                    <div>
                      <span className="font-medium">Updated:</span>{" "}
                      {new Date(progress.updated_at).toLocaleString()}
                    </div>
                  )}
                  {progress.completed_at && (
                    <div>
                      <span className="font-medium">Completed:</span>{" "}
                      {new Date(progress.completed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Select a previous import above to view its status.</p>
          )}
        </>
      )}
    </div>
  );
};

export default FlowImport;
